import { useEffect, useState, useRef } from "react";
import { API } from "../../lib/api";

export const AIAssistant = () => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello. I'm your CEO Control Room assistant. Ask me about candidate pipelines, compliance, job orders or recruitment strategy. All suggestions are decision-support only and require your approval." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const token = localStorage.getItem("azimi_admin_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      sessionRef.current = res.headers.get("X-Session-Id") || sessionRef.current;
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The assistant is temporarily unavailable. Please try again shortly." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-[70vh] flex-col rounded-2xl border border-border bg-white" data-testid="ai-assistant">
      <div className="border-b border-border px-6 py-4">
        <h3 className="font-serif text-lg text-navy">CEO Control Room AI Assistant</h3>
        <p className="text-xs text-muted-foreground">Powered by OpenAI · decision-support only, human approval required</p>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-navy text-white" : "border border-border bg-secondary/40 text-charcoal"}`} data-testid={`ai-msg-${m.role}`}>
              {m.content || (busy && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="border-t border-border p-4">
        <div className="flex gap-3">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask the control room assistant…"
            className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/15"
            data-testid="ai-input"
          />
          <button onClick={send} disabled={busy} className="rounded-lg bg-gold px-5 py-2.5 text-sm font-semibold text-white hover:bg-gold-dark disabled:opacity-60" data-testid="ai-send">
            {busy ? "…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
